import type { HTMLAttributes } from 'react';
import { cn } from '../../../../lib/utils.js';

export type ProgressIndicatorStepState = 'completed' | 'active' | 'upcoming';

export type ProgressIndicatorStep = {
  id: string;
  label: string;
  state: ProgressIndicatorStepState;
};

export interface ProgressIndicatorProps extends HTMLAttributes<HTMLDivElement> {
  steps: ProgressIndicatorStep[];
  showLabels?: boolean;
}

export function ProgressIndicator({ steps, showLabels = true, className, ...props }: ProgressIndicatorProps) {
  return (
    <div className={cn('flex items-center justify-center', className)} {...props}>
      {steps.map((step, index) => {
        const isCompleted = step.state === 'completed';
        const isActive = step.state === 'active';
        const showConnector = index < steps.length - 1;

        return (
          <div key={step.id} className="flex items-center">
            <div className="flex shrink-0 items-center gap-2">
              <span
                className={cn(
                  'inline-flex h-8 w-8 items-center justify-center rounded-full border text-[12px] font-semibold transition',
                  isCompleted && 'border-ig-accent bg-ig-accent text-[var(--ig-accent-text)]',
                  isActive && 'border-2 border-ig-accent bg-transparent text-ig-accent shadow-[0_0_0_4px_var(--ig-accent-muted)]',
                  !isCompleted && !isActive && 'border-ig-border bg-ig-bg-tertiary text-ig-text-tertiary'
                )}
                aria-current={isActive ? 'step' : undefined}
              >
                {index + 1}
              </span>
              {showLabels ? (
                <span
                  className={cn(
                    'whitespace-nowrap text-[12px] font-medium',
                    isCompleted || isActive ? 'text-ig-text-primary' : 'text-ig-text-tertiary'
                  )}
                >
                  {step.label}
                </span>
              ) : null}
            </div>
            {showConnector ? (
              <span
                className={cn(
                  'mx-2 h-px w-10 sm:w-14 md:w-20',
                  isCompleted ? 'bg-ig-accent' : 'bg-ig-border'
                )}
                aria-hidden
              />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
